import { useState, useEffect, useCallback } from 'react';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import CaseViewModal from '@/components/CaseViewModal';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import type { CaseRow } from '@/components/CaseViewModal';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import { useYear } from '@/context/YearContext';
import type { Case, CaseStats } from '@/types';

/* ─── Constants ─── */

const TABS = ['All', 'New', 'Assigned', 'In Progress', 'Resolved'];

const STAT_LABELS = ['New', 'Assigned', 'In Progress', 'Resolved'] as const;

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        caseNumber: c.caseNumber,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

/* ─── Component ─── */

export default function MediatorMyCases() {
    const { yearQuery } = useYear();
    const [activeTab, setActiveTab] = useState('All');
    const [cases, setCases] = useState<CaseRow[]>([]);
    const [stats, setStats] = useState<CaseStats | null>(null);
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchCases = useCallback(() => {
        const statusParam = activeTab !== 'All' ? `&status=${encodeURIComponent(activeTab)}` : '';
        const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
        api.get<Case[]>(`/api/cases/my${yearQuery}&limit=100${statusParam}${searchParam}`)
            .then((res) => setCases((res.response.data ?? []).map(mapToRow)))
            .catch(() => setCases([]));
    }, [activeTab, yearQuery, debouncedSearch]);

    const fetchStats = useCallback(() => {
        api.get<CaseStats>(`/api/cases/my/stats${yearQuery}`)
            .then((res) => setStats(res.response.data ?? null))
            .catch(() => {});
    }, [yearQuery]);

    useEffect(() => {
        fetchCases();
    }, [fetchCases]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleCaseUpdated = () => {
        fetchCases();
        fetchStats();
    };

    const statsCards = STAT_LABELS.map((label) => ({
        label,
        value: String(stats?.[label as keyof CaseStats] ?? 0),
    }));

    const COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'caseNumber', label: 'Case Number' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:justify-between lg:px-8">
                <h1 className="text-2xl font-bold text-secondary sm:text-3xl">My Cases</h1>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Cases"
                columns={COLUMNS}
                data={cases}
                getRowKey={(row) => row.id}
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                onExportCSV={() => exportCSV('my-cases', COLUMNS, cases)}
            />

            <CaseViewModal
                open={!!selectedCase}
                onClose={() => setSelectedCase(null)}
                caseData={selectedCase}
                onCaseUpdated={handleCaseUpdated}
            />
        </div>
    );
}
